import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { useApi } from '../../api-context.js';
import { parseOrThrow } from '../../api/contract.js';
import {
  importReportSchema,
  importResultSchema,
  type ImportReport,
  type ImportResult,
} from '../../../modules/reference/api/schemas.js';

/**
 * The two calls an import makes.
 *
 * Deliberately not `useApiMutation`: that helper posts JSON and attaches an
 * idempotency key, and neither applies here. A multipart body is not JSON, and
 * the dry run is idempotent by construction — it writes nothing — while the
 * commit is made single-use by the server discarding the plan.
 */
export function useImportDryRun(basePath: string): UseMutationResult<ImportReport, unknown, File> {
  const api = useApi();

  return useMutation({
    mutationFn: async (file: File): Promise<ImportReport> => {
      const body = new FormData();
      body.append('file', file);

      const answer = await api.postForm<unknown>(`${basePath}/import`, body);
      return parseOrThrow(importReportSchema, answer, `${basePath}/import`);
    },
  });
}

export function useImportCommit(
  basePath: string,
): UseMutationResult<ImportResult, unknown, string> {
  const api = useApi();

  return useMutation({
    mutationFn: async (importId: string): Promise<ImportResult> => {
      const answer = await api.post<unknown>(`${basePath}/import/${importId}`, {});
      return parseOrThrow(importResultSchema, answer, `${basePath}/import/${importId}`);
    },
  });
}
